import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, MoreHorizontal, Check, Plus, Users, Trash2, Copy, Edit2, X } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
export interface SavedView {
  id: string;
  name: string;
  type: 'default' | 'mine' | 'team';
  creator?: string;
  isShared?: boolean;
}
interface SavedViewsDropdownProps {
  currentView: string;
  onViewSelect: (view: SavedView) => void;
  onSaveView: (name: string, isShared: boolean) => void;
  onDeleteView?: (viewId: string) => void;
  onDuplicateView?: (viewId: string) => void;
  onRenameView?: (viewId: string, newName: string) => void;
  hasActiveFilters: boolean;
  savedViews: SavedView[];
  className?: string;
}
export function SavedViewsDropdown({
  currentView,
  onViewSelect,
  onSaveView,
  onDeleteView,
  onDuplicateView,
  onRenameView,
  hasActiveFilters,
  savedViews,
  className = ''
}: SavedViewsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [renamingViewId, setRenamingViewId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsSaving(false);
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close overflow menu when clicking outside
  useEffect(() => {
    function handleClickOutsideMenu(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    }
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutsideMenu);
    }
    return () => document.removeEventListener('mousedown', handleClickOutsideMenu);
  }, [openMenuId]);
  // Focus rename input when entering rename mode
  useEffect(() => {
    if (renamingViewId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingViewId]);
  const handleStartRename = (view: SavedView) => {
    setRenamingViewId(view.id);
    setRenameValue(view.name);
    setOpenMenuId(null);
  };
  const handleConfirmRename = () => {
    if (renamingViewId && renameValue.trim()) {
      onRenameView?.(renamingViewId, renameValue.trim());
    }
    setRenamingViewId(null);
    setRenameValue('');
  };
  const handleCancelRename = () => {
    setRenamingViewId(null);
    setRenameValue('');
  };
  const handleSave = () => {
    if (newViewName.trim()) {
      onSaveView(newViewName, isShared);
      setIsSaving(false);
      setNewViewName('');
      setIsShared(false);
      setIsOpen(false);
    }
  };
  const handleCancelSave = () => {
    setIsSaving(false);
    setNewViewName('');
    setIsShared(false);
  };
  const defaultView = savedViews.find(v => v.type === 'default') || {
    id: 'all',
    name: 'All Subscriptions',
    type: 'default'
  };
  const myViews = savedViews.filter(v => v.type === 'mine');
  const teamViews = savedViews.filter(v => v.type === 'team');
  const renderViewItem = (view: SavedView) => {
    const isActive = currentView === view.name;
    const isDefault = view.type === 'default';
    const isRenaming = renamingViewId === view.id;
    if (isRenaming) {
      return <div key={view.id} className="flex items-center gap-2 px-3 py-2 rounded-md" data-id="element-2384">
          <input ref={renameInputRef} type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') handleConfirmRename();
          if (e.key === 'Escape') handleCancelRename();
        }} className="flex-1 h-7 px-2 text-sm border border-[#0EA5E9] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white" data-id="element-2385" />
          <button onClick={handleConfirmRename} disabled={!renameValue.trim()} className="p-1 rounded-md text-[#10B981] hover:bg-[#D1FAE5] transition-colors disabled:opacity-40" data-id="element-2386">
            <Check className="w-4 h-4" data-id="element-2387" />
          </button>
          <button onClick={handleCancelRename} className="p-1 rounded-md text-[#94A3B8] hover:bg-[#F1F5F9] transition-colors" data-id="element-2388">
            <X className="w-4 h-4" data-id="element-2389" />
          </button>
        </div>;
    }
    return <div key={view.id} className={`group flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors ${isActive ? 'bg-[#F0F9FF]' : 'hover:bg-[#F8FAFC]'}`} onClick={() => {
      onViewSelect(view);
      setIsOpen(false);
    }} data-id="element-2390">
        <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-2391">
          {isActive && <div className="w-1 h-4 bg-[#0EA5E9] rounded-full flex-shrink-0" data-id="element-2392" />}
          <div className={`flex flex-col min-w-0 ${!isActive ? 'pl-3' : ''}`} data-id="element-2393">
            <span className={`text-sm truncate ${isActive ? 'font-medium text-[#0EA5E9]' : 'text-[#0F172A]'}`} data-id="element-2394">
              {view.name}
            </span>
            {view.type === 'team' && <div className="flex items-center gap-1.5 mt-0.5" data-id="element-2395">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]" data-id="element-2396">
                  Shared
                </span>
                <span className="text-[11px] text-[#94A3B8] truncate" data-id="element-2397">
                  by {view.creator}
                </span>
              </div>}
          </div>
        </div>

        {!isDefault && <div className="relative" onClick={e => e.stopPropagation()} data-id="element-2398">
            <button onClick={() => setOpenMenuId(openMenuId === view.id ? null : view.id)} className={`p-1 rounded-md text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#E2E8F0] opacity-0 group-hover:opacity-100 transition-all ${openMenuId === view.id ? 'opacity-100 bg-[#E2E8F0]' : ''}`} data-id="element-2399">
              <MoreHorizontal className="w-4 h-4" data-id="element-2400" />
            </button>

            {openMenuId === view.id && <div ref={menuRef} className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-[#E2E8F0] z-50 py-1 overflow-hidden" data-id="element-2401">
                {view.type === 'mine' && <button onClick={() => handleStartRename(view)} className="w-full px-3 py-2 text-left text-[13px] text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2" data-id="element-2402">
                    <Edit2 className="w-3.5 h-3.5 text-[#64748B]" data-id="element-2403" />
                    Rename
                  </button>}
                <button onClick={() => {
            onDuplicateView?.(view.id);
            setOpenMenuId(null);
          }} className="w-full px-3 py-2 text-left text-[13px] text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2" data-id="element-2404">
                  <Copy className="w-3.5 h-3.5 text-[#64748B]" data-id="element-2405" />
                  Duplicate
                </button>
                {view.type === 'mine' && <button onClick={() => {
            onDeleteView?.(view.id);
            setOpenMenuId(null);
          }} className="w-full px-3 py-2 text-left text-[13px] text-[#EF4444] hover:bg-[#FEF2F2] flex items-center gap-2 border-t border-[#E2E8F0] mt-1" data-id="element-2406">
                    <Trash2 className="w-3.5 h-3.5" data-id="element-2407" />
                    Delete
                  </button>}
                {view.type === 'team' && <button onClick={() => {
            // Implement remove from my views logic
            setOpenMenuId(null);
          }} className="w-full px-3 py-2 text-left text-[13px] text-[#EF4444] hover:bg-[#FEF2F2] flex items-center gap-2 border-t border-[#E2E8F0] mt-1" data-id="element-2408">
                    <X className="w-3.5 h-3.5" data-id="element-2409" />
                    Remove
                  </button>}
              </div>}
          </div>}
      </div>;
  };
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2410">
      <Button variant="outline" size="md" onClick={() => setIsOpen(!isOpen)} className={`justify-between min-w-[160px] ${isOpen ? 'bg-[#F8FAFC] ring-2 ring-[#E0F2FE] border-[#0EA5E9]' : ''}`} iconRight={<ChevronDown className={`w-4 h-4 text-[#64748B] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2412" />} data-id="element-2411">
        <span className="truncate max-w-[140px]" data-id="element-2413">{'Views'}</span>
      </Button>

      {isOpen && <div className="absolute top-full left-0 mt-2 w-[280px] bg-white border border-[#E2E8F0] rounded-lg shadow-[0_4px_12px_rgba(15,23,42,0.1)] z-[100] animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-2414">
          <div className="p-2 space-y-1" data-id="element-2415">
            {/* Default View */}
            {renderViewItem(defaultView)}

            {/* My Views */}
            {myViews.length > 0 && <div className="mt-3" data-id="element-2416">
                <div className="px-3 py-1.5 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-2417">
                  My Views
                </div>
                {myViews.map(renderViewItem)}
              </div>}
          </div>

          {/* Footer / Save View Form */}
          {(hasActiveFilters || isSaving) && <div className="border-t border-[#E2E8F0] p-3 bg-[#F8FAFC] rounded-b-lg" data-id="element-2418">
              {isSaving ? <div className="space-y-3" data-id="element-2419">
                  <div data-id="element-2420">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2421">
                      View Name
                    </label>
                    <input type="text" value={newViewName} onChange={e => setNewViewName(e.target.value)} placeholder="e.g. VIP Failed Payments" className="w-full h-8 px-3 text-sm border border-[#CBD5E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-[#0EA5E9]" autoFocus data-id="element-2422" />
                  </div>

                  <div className="flex items-center gap-2" data-id="element-2423">
                    <button onClick={() => setIsShared(!isShared)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${isShared ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-2424">
                      <span className="inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200" style={{
                transform: isShared ? 'translateX(22px)' : 'translateX(4px)'
              }} data-id="element-2425" />
                    </button>
                    <span className="text-[13px] text-[#475569]" data-id="element-2426">
                      Share with team
                    </span>
                  </div>

                  <div className="flex items-center gap-2 pt-1" data-id="element-2427">
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={!newViewName.trim()} className="flex-1" data-id="element-2428">
                      Save
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleCancelSave} className="flex-1" data-id="element-2429">
                      Cancel
                    </Button>
                  </div>
                </div> : <button onClick={() => setIsSaving(true)} className="w-full flex items-center justify-center gap-2 text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] py-1 transition-colors" data-id="element-2430">
                  <Plus className="w-4 h-4" data-id="element-2431" />
                  Save Current View
                </button>}
            </div>}
        </div>}
    </div>;
}